/**
 * AI Artifacts Store Contracts (SCH-3182)
 *
 * The pages AI skills publish live in the ops store. Credential callers (a `session` credential bound to a
 * person, or the `backend` credential) reach it over three `POST` HTTP actions under the `artifacts:write`
 * scope (plan KTD8): `/artifacts/publish`, `/artifacts/list` and `/artifacts/revoke`. Viewers reach it through
 * `aiArtifacts:open`, whose answer is `ArtifactOpenResponseSchema`.
 *
 * Agents publish team-only (KD8, R5). The publish request is `.strict()`, so a body carrying `grants`,
 * `publicUntil`, a public flag or `expiresAt` fails the parse, and the publish route answers
 * `audience_not_allowed` and records nothing (AE5). A publish may only suggest recipients; a member approves
 * them in Wayfinder.
 *
 * An artifact is identified to its publisher by (kind, key): publish upserts on it (R2) and revoke names it.
 * Viewers know it only by `publicId`. Refusals follow the store's convention: HTTP actions answer
 * `{ error: code }`. Ids are Convex ids on the store side, typed as strings here; timestamps are epoch ms.
 *
 * Interactive artifacts (SCH-3664) add the `interactive` kind: pages with live data beside them (artifact-data.ts)
 * that a page reads and writes through the viewer's port (artifact-bridge.ts). For that kind, a team member's
 * agent creates through publish, then updates, copies, reads, makes public and checks by `publicId` through the
 * routes below.
 *
 * Version history (SCH-4882): every version of an interactive artifact is kept (the last 25), listed by
 * `POST /artifacts/versions` and the member query `aiArtifacts:versions`, previewed by a team member through
 * `aiArtifacts:previewVersion` (a content link, never inline HTML), and restored by `POST /artifacts/restore`, which
 * republishes a past version's page as the next version, or by a team member in the viewer through the action
 * `aiArtifacts:restoreVersion` (SCH-4946), which never hides data.
 *
 * Plans: docs/plans/2026-10-15-1604-feat-ai-artifacts-store-plan.md (U1),
 * docs/plans/2026-10-19-1623-feat-ai-artifacts-live-data-plan.md (U1).
 */

import { z } from 'zod'
import { ArtifactDataKeyRefSchema, ArtifactDataManifestSchema } from './artifact-data'

// =============================================================================
// Shared primitives
// =============================================================================

/**
 * The kinds registry (KTD15). The store sets each kind's default and maximum expiry and refuses any other.
 * `interactive` pages carry live data and do not expire (SCH-3664 KTD1).
 */
export const ArtifactKindSchema = z.enum([
  'customer-pulse-snapshot',
  'customer-pulse-school-update',
  'customer-engagement-report',
  'blackbaud-bug-report',
  'adhoc',
  'interactive'
])
export type ArtifactKind = z.infer<typeof ArtifactKindSchema>

export const ARTIFACT_KEY_MAX_LENGTH = 128

/**
 * The caller's key for an artifact, unique within its kind. Lower-case letters, digits, `.`, `_` and `-`,
 * starting with a letter or digit, at most 128 characters: safe in a path, a log line and a CLI argument.
 * Upper case is refused rather than folded, so two spellings never name one artifact.
 */
export const ArtifactKeySchema = z
  .string()
  .max(ARTIFACT_KEY_MAX_LENGTH)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, 'lower-case letters, digits, ".", "_" and "-", starting with a letter or digit')

const ArtifactTitleSchema = z.string().trim().min(1).max(200)
const SchoolIdSchema = z.string().min(1)
/** The id the viewer link carries (`https://share.schooljourney.com/a/<publicId>`). */
const ArtifactPublicIdSchema = z.string().min(1).max(64)
const EpochMsSchema = z.number().int().nonnegative()
const VersionSchema = z.number().int().positive()

/** How a view was let in (KTD7): a member, a named or school-admin grant, or the public window. */
export const ArtifactAudienceSchema = z.enum(['team', 'grant', 'public'])
export type ArtifactAudience = z.infer<typeof ArtifactAudienceSchema>

/**
 * A recipient a publisher suggests; a member approves it in Wayfinder before it becomes a grant (KD8).
 * `schoolAdminsOf` expands to that school's admins at approval time (KD3). Emails are trimmed and
 * lower-cased, as grants match on a lower-cased verified email (KTD7).
 */
export const ArtifactSuggestedGrantSchema = z.union([
  z.object({ email: z.string().trim().toLowerCase().email() }).strict(),
  z.object({ schoolAdminsOf: SchoolIdSchema }).strict()
])
export type ArtifactSuggestedGrant = z.infer<typeof ArtifactSuggestedGrantSchema>

// =============================================================================
// Refusals
// =============================================================================

/**
 * Why `aiArtifacts:open` refused (KTD7). None of them returns the title (R12).
 * - `sign_in_required`: no Clerk identity, or one from a template without the `sj_artifacts` marker.
 * - `email_unverified`: the grant could only match on an email Clerk has not verified.
 * - `impersonation`: the session carries an actor claim (R10).
 * - `human_only`: a store credential tried to open.
 * - `updating`: grant or public access while a republish awaits re-approval (KTD9).
 */
export const ArtifactOpenRefusalReasonSchema = z.enum([
  'sign_in_required',
  'email_unverified',
  'impersonation',
  'human_only',
  'no_access',
  'updating',
  'rate_limited',
  'not_found',
  'revoked',
  'expired'
])
export type ArtifactOpenRefusalReason = z.infer<typeof ArtifactOpenRefusalReasonSchema>

/**
 * What the page check found (R3). The check names the kind of finding, never the matched text, so a secret
 * it caught is not echoed back to the caller or into a log.
 */
export const ArtifactPageCheckReasonSchema = z.enum([
  'page_too_large',
  'page_secret',
  'page_external_resource',
  'page_form',
  'page_webrtc',
  'page_resource_hint',
  'page_high_entropy_token'
])
export type ArtifactPageCheckReason = z.infer<typeof ArtifactPageCheckReasonSchema>

/**
 * Every refusal the three credential routes give, as `{ error: code }`.
 * - `invalid_request`: the body failed its schema for any reason other than an audience field.
 * - `unknown_kind`: the kind is not in the store's registry.
 * - `audience_not_allowed`: the body carried grants, a public window or an expiry (R5, AE5).
 * - `forbidden_identity`: a session credential with no bound person (KTD8).
 * - `forbidden_scope`: the credential lacks `artifacts:write`.
 * - `forbidden_owner`: a republish or revoke by someone other than the original publisher (KTD9).
 * - `revoked`, `expired`: a republish of an artifact past either; publish under a new key.
 * - `not_found`: revoke named no artifact of this caller's.
 *
 * Interactive artifacts (SCH-3664) add:
 * - `already_exists`: a create of an interactive (kind, key) that exists and is not a retry of the same create.
 * - `kind_mismatch`: an update, copy or data call on an artifact that is not interactive.
 * - `school_change_refused`: an update that changes the school, or a copy carrying data to another school.
 * - `version_conflict`: an update whose `ifVersion` is not the current version, or a copy whose source moved.
 * - `would_hide_data`: an update that would hide items holding data, without `allowHide` (names the keys).
 * - `person_required`: a credential not bound to a current member's person (the backend credential included).
 * - `undeclared_key`: a data write to a key the page does not declare.
 * - `limit_exceeded`: a data write past a key, value or item limit.
 * - `not_expiring`: an expiry change on a kind that does not expire.
 */
export const ArtifactWriteRefusalReasonSchema = z.enum([
  'invalid_request',
  'unknown_kind',
  'audience_not_allowed',
  'forbidden_identity',
  'forbidden_scope',
  'forbidden_owner',
  'revoked',
  'expired',
  'not_found',
  'already_exists',
  'kind_mismatch',
  'school_change_refused',
  'version_conflict',
  'would_hide_data',
  'person_required',
  'undeclared_key',
  'limit_exceeded',
  'not_expiring',
  ...ArtifactPageCheckReasonSchema.options
])
export type ArtifactWriteRefusalReason = z.infer<typeof ArtifactWriteRefusalReasonSchema>

export const ArtifactWriteErrorResponseSchema = z.object({ error: ArtifactWriteRefusalReasonSchema }).strict()
export type ArtifactWriteErrorResponse = z.infer<typeof ArtifactWriteErrorResponseSchema>

/** Why an interactive page's data manifest is refused at create or update (KTD5). */
export const ArtifactManifestRefusalReasonSchema = z.enum([
  'malformed_key',
  'duplicate_key',
  'too_many_keys',
  'bad_data_block',
  'orphan_item'
])
export type ArtifactManifestRefusalReason = z.infer<typeof ArtifactManifestRefusalReasonSchema>

/**
 * A create or update whose page declares a bad data manifest (KTD5): `invalid_request`, with the reason and the key it
 * is about, `piece` or `piece/item` (a malformed key repeated up to 80 characters; empty when the `application/sj-data`
 * block itself does not parse).
 */
export const ArtifactManifestErrorResponseSchema = z
  .object({
    error: z.literal('invalid_request'),
    reason: ArtifactManifestRefusalReasonSchema,
    key: z.string().max(160)
  })
  .strict()
export type ArtifactManifestErrorResponse = z.infer<typeof ArtifactManifestErrorResponseSchema>

/** What the publish route refuses with: a bad manifest naming its key, or any write refusal as a plain `{ error }`. */
export const ArtifactPublishErrorResponseSchema = z.union([
  ArtifactManifestErrorResponseSchema,
  ArtifactWriteErrorResponseSchema
])
export type ArtifactPublishErrorResponse = z.infer<typeof ArtifactPublishErrorResponseSchema>

/** A `would_hide_data` refusal names the keys whose stored data the new page would hide (KTD2). */
export const ArtifactWouldHideDataErrorResponseSchema = z
  .object({ error: z.literal('would_hide_data'), keys: z.array(ArtifactDataKeyRefSchema).min(1) })
  .strict()
export type ArtifactWouldHideDataErrorResponse = z.infer<typeof ArtifactWouldHideDataErrorResponseSchema>

/**
 * What the update route refuses with: `would_hide_data` with its keys, or any other write refusal as a plain
 * `{ error }`. The plain branch excludes `would_hide_data`, so that refusal can never omit the keys.
 */
export const ArtifactUpdateErrorResponseSchema = z.union([
  ArtifactWouldHideDataErrorResponseSchema,
  ArtifactManifestErrorResponseSchema,
  z.object({ error: ArtifactWriteRefusalReasonSchema.exclude(['would_hide_data']) }).strict()
])
export type ArtifactUpdateErrorResponse = z.infer<typeof ArtifactUpdateErrorResponseSchema>

// =============================================================================
// Publish
// =============================================================================

/**
 * `POST /artifacts/publish`: upsert on (kind, key). Strict: an audience or expiry field fails the parse
 * (the route answers `audience_not_allowed`). The size cap and page check run in the store (R3).
 */
export const ArtifactPublishRequestSchema = z
  .object({
    kind: ArtifactKindSchema,
    key: ArtifactKeySchema,
    title: ArtifactTitleSchema,
    schoolId: SchoolIdSchema.nullable(),
    html: z.string().min(1),
    suggestedGrants: z.array(ArtifactSuggestedGrantSchema).optional()
  })
  .strict()
export type ArtifactPublishRequest = z.infer<typeof ArtifactPublishRequestSchema>

export const ArtifactPublishResponseSchema = z
  .object({
    artifactId: z.string().min(1),
    publicId: ArtifactPublicIdSchema,
    version: VersionSchema,
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactPublishResponse = z.infer<typeof ArtifactPublishResponseSchema>

// =============================================================================
// List and revoke
// =============================================================================

/** `POST /artifacts/list`: the caller's own artifacts only; listing everything is Wayfinder's (KTD8). */
export const ArtifactListRequestSchema = z.object({}).strict()
export type ArtifactListRequest = z.infer<typeof ArtifactListRequestSchema>

export const ArtifactListItemSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    kind: ArtifactKindSchema,
    key: ArtifactKeySchema,
    title: z.string(),
    schoolId: SchoolIdSchema.nullable(),
    version: VersionSchema,
    expiresAt: EpochMsSchema,
    revokedAt: EpochMsSchema.nullable(),
    viewerUrl: z.string().url(),
    createdAt: EpochMsSchema,
    updatedAt: EpochMsSchema
  })
  .strict()
export type ArtifactListItem = z.infer<typeof ArtifactListItemSchema>

export const ArtifactListResponseSchema = z.object({ artifacts: z.array(ArtifactListItemSchema) }).strict()
export type ArtifactListResponse = z.infer<typeof ArtifactListResponseSchema>

/**
 * A thread label the CLI supplies for the audience record (KTD10). It is the caller's say-so, shown as unverified,
 * and capped at 120 characters.
 */
export const ARTIFACT_THREAD_LABEL_MAX_LENGTH = 120
const ThreadLabelSchema = z.string().trim().min(1).max(ARTIFACT_THREAD_LABEL_MAX_LENGTH)

/**
 * `POST /artifacts/revoke`: names the artifact as publish does, by (kind, key). Revoking an `interactive` artifact
 * deletes its live data, so it is its owner's only and needs `deleteData: true` (KTD3); without it the store refuses
 * `invalid_request`. The other kinds ignore it. `thread` goes on an interactive revoke's audience event (KTD10).
 */
export const ArtifactRevokeRequestSchema = z
  .object({
    kind: ArtifactKindSchema,
    key: ArtifactKeySchema,
    deleteData: z.boolean().optional(),
    thread: ThreadLabelSchema.optional()
  })
  .strict()
export type ArtifactRevokeRequest = z.infer<typeof ArtifactRevokeRequestSchema>

export const ArtifactRevokeResponseSchema = z.object({ ok: z.literal(true) }).strict()
export type ArtifactRevokeResponse = z.infer<typeof ArtifactRevokeResponseSchema>

// =============================================================================
// Interactive routes (SCH-3664 KTD2, KTD10–KTD12)
// =============================================================================

/** A note on a new version (SCH-4882): the publisher's untrusted text, shown as text and never followed. */
export const ARTIFACT_VERSION_NOTE_MAX_LENGTH = 280
const ArtifactVersionNoteSchema = z.string().trim().min(1).max(ARTIFACT_VERSION_NOTE_MAX_LENGTH)

/**
 * `POST /artifacts/update`: the next version of an interactive artifact, named only by its link. `ifVersion` is
 * required and must be the current version; `allowHide` lets the update hide items holding data; `note` is kept on
 * the new version's history row.
 */
export const ArtifactUpdateRequestSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    ifVersion: VersionSchema,
    title: ArtifactTitleSchema,
    schoolId: SchoolIdSchema.nullable(),
    html: z.string().min(1),
    allowHide: z.boolean().optional(),
    thread: ThreadLabelSchema.optional(),
    note: ArtifactVersionNoteSchema.optional()
  })
  .strict()
export type ArtifactUpdateRequest = z.infer<typeof ArtifactUpdateRequestSchema>

/** `changed: false` is a same-bytes update: a no-op that keeps the version. */
export const ArtifactUpdateResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    version: VersionSchema,
    changed: z.boolean(),
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactUpdateResponse = z.infer<typeof ArtifactUpdateResponseSchema>

/**
 * `POST /artifacts/copy`: a new, team-only interactive artifact from an existing one (KTD11). `key` makes a retry
 * return the same copy. The copy keeps the source's school and title unless given; `carryData` copies the data,
 * and only within one school.
 */
export const ArtifactCopyRequestSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    key: ArtifactKeySchema,
    title: ArtifactTitleSchema.optional(),
    schoolId: SchoolIdSchema.nullable().optional(),
    carryData: z.boolean().optional()
  })
  .strict()
export type ArtifactCopyRequest = z.infer<typeof ArtifactCopyRequestSchema>

export const ArtifactCopyResponseSchema = ArtifactPublishResponseSchema
export type ArtifactCopyResponse = z.infer<typeof ArtifactCopyResponseSchema>

/** `POST /artifacts/get`: an artifact's metadata, manifest, public state and current page HTML. */
export const ArtifactGetRequestSchema = z.object({ publicId: ArtifactPublicIdSchema }).strict()
export type ArtifactGetRequest = z.infer<typeof ArtifactGetRequestSchema>

export const ArtifactGetResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    kind: ArtifactKindSchema,
    key: ArtifactKeySchema,
    title: z.string(),
    schoolId: SchoolIdSchema.nullable(),
    version: VersionSchema,
    publishedBy: z.string(),
    manifest: ArtifactDataManifestSchema,
    /** Public until this time; null when not public. */
    publicUntil: EpochMsSchema.nullable(),
    expiresAt: EpochMsSchema,
    revokedAt: EpochMsSchema.nullable(),
    html: z.string(),
    viewerUrl: z.string().url(),
    createdAt: EpochMsSchema,
    updatedAt: EpochMsSchema
  })
  .strict()
export type ArtifactGetResponse = z.infer<typeof ArtifactGetResponseSchema>

/** `POST /artifacts/public`: make an interactive artifact public (`public: true`) or private (KD4, R16, R17). */
export const ArtifactPublicRequestSchema = z
  .object({ publicId: ArtifactPublicIdSchema, public: z.boolean(), thread: ThreadLabelSchema.optional() })
  .strict()
export type ArtifactPublicRequest = z.infer<typeof ArtifactPublicRequestSchema>

/** `changed: false` when the artifact was already in the asked state (no audience event is written). */
export const ArtifactPublicResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    publicUntil: EpochMsSchema.nullable(),
    changed: z.boolean(),
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactPublicResponse = z.infer<typeof ArtifactPublicResponseSchema>

/**
 * The audience changes R17 records (KTD10), a restore of a past version (SCH-4882), recorded whatever the audience, and
 * an agent's share and unshare (`POST /artifacts/share` and `/artifacts/unshare`, SCH-5038).
 */
export const ArtifactAudienceEventActionSchema = z.enum([
  'made_public',
  'made_private',
  'updated_while_public',
  'updated_while_shared',
  'revoked',
  'restored',
  'shared',
  'unshared'
])
export type ArtifactAudienceEventAction = z.infer<typeof ArtifactAudienceEventActionSchema>

/**
 * `POST /artifacts/history`: the artifact's audience events, oldest first, a page at a time. Without `cursor` it answers
 * the first page; pass the previous answer's `continueCursor` for the next, until it is null.
 */
export const ArtifactHistoryRequestSchema = z
  .object({ publicId: ArtifactPublicIdSchema, cursor: z.string().min(1).optional() })
  .strict()
export type ArtifactHistoryRequest = z.infer<typeof ArtifactHistoryRequestSchema>

/**
 * One audience event: who (the member), through which credential (`session:<host>`), the thread label the CLI
 * supplied (unverified, null when none was given) and when.
 */
export const ArtifactAudienceEventSchema = z
  .object({
    action: ArtifactAudienceEventActionSchema,
    version: VersionSchema,
    actor: z.string().min(1),
    credential: z.string().min(1),
    unverifiedThread: z.string().max(ARTIFACT_THREAD_LABEL_MAX_LENGTH).nullable(),
    at: EpochMsSchema
  })
  .strict()
export type ArtifactAudienceEvent = z.infer<typeof ArtifactAudienceEventSchema>

/** One page of events; `continueCursor` is null on the last page. */
export const ArtifactHistoryResponseSchema = z
  .object({ events: z.array(ArtifactAudienceEventSchema), continueCursor: z.string().min(1).nullable() })
  .strict()
export type ArtifactHistoryResponse = z.infer<typeof ArtifactHistoryResponseSchema>

// =============================================================================
// Agent share (SCH-5038)
// =============================================================================

/** How many emails one agent share names at most (the backend lookup's cap, `RESOLVE_PEOPLE_MAX_EMAILS`). */
export const ARTIFACT_SHARE_MAX_EMAILS = 50
/** The longest idempotency key a share or unshare takes. */
export const ARTIFACT_SHARE_IDEMPOTENCY_KEY_MAX_LENGTH = 200

const ShareEmailSchema = z.string().trim().toLowerCase().email()
const ShareIdempotencyKeySchema = z.string().min(1).max(ARTIFACT_SHARE_IDEMPOTENCY_KEY_MAX_LENGTH)

/**
 * `POST /artifacts/share`, under `artifacts:share`: grant named people access to an interactive artifact. Accounts only:
 * the store asks the backend which emails name a School Journey account and refuses the whole request (`no_account`,
 * naming them) when any does not. A share approves the current version and writes a `shared` audience event naming the
 * member, the credential and the thread label. It never makes the page public. A retry with the same key changes
 * nothing.
 */
export const ArtifactShareRequestSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    emails: z.array(ShareEmailSchema).min(1).max(ARTIFACT_SHARE_MAX_EMAILS),
    idempotencyKey: ShareIdempotencyKeySchema,
    thread: ThreadLabelSchema.optional()
  })
  .strict()
export type ArtifactShareRequest = z.infer<typeof ArtifactShareRequestSchema>

/** Who now has access: each email's account. `changed: false` for a retry, which wrote nothing. */
export const ArtifactShareResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    granted: z.array(
      z.object({ email: z.string().min(1), platformUserId: z.string().min(1), name: z.string() }).strict()
    ),
    changed: z.boolean(),
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactShareResponse = z.infer<typeof ArtifactShareResponseSchema>

/** `POST /artifacts/unshare`, under `artifacts:share`: revoke `email`'s grant; writes an `unshared` audience event. */
export const ArtifactUnshareRequestSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    email: ShareEmailSchema,
    idempotencyKey: ShareIdempotencyKeySchema,
    thread: ThreadLabelSchema.optional()
  })
  .strict()
export type ArtifactUnshareRequest = z.infer<typeof ArtifactUnshareRequestSchema>

/** `changed: false` for a retry, which wrote nothing. An email with no active grant is refused `not_found`. */
export const ArtifactUnshareResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    email: z.string().min(1),
    changed: z.boolean(),
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactUnshareResponse = z.infer<typeof ArtifactUnshareResponseSchema>

/**
 * The share routes' own refusals, beside the write refusals: `no_account` names the emails with no School Journey
 * account (the request granted nothing), `lookup_unconfigured` means the store holds no backend lookup key, and
 * `lookup_failed` that the backend lookup did not answer. `reason` is a sentence for the person.
 */
export const ArtifactShareRefusalReasonSchema = z.enum(['no_account', 'lookup_unconfigured', 'lookup_failed'])
export type ArtifactShareRefusalReason = z.infer<typeof ArtifactShareRefusalReasonSchema>

export const ArtifactShareErrorResponseSchema = z.union([
  z
    .object({ error: z.literal('no_account'), emails: z.array(z.string().min(1)).min(1), reason: z.string().min(1) })
    .strict(),
  z.object({ error: ArtifactShareRefusalReasonSchema.exclude(['no_account']), reason: z.string().min(1) }).strict(),
  ArtifactWriteErrorResponseSchema
])
export type ArtifactShareErrorResponse = z.infer<typeof ArtifactShareErrorResponseSchema>

/**
 * `POST /artifacts/check`: a dry run that writes nothing. It runs the page check and extracts the manifest; with
 * `publicId`, it also lists the keys whose data an update to this page would hide.
 */
export const ArtifactCheckRequestSchema = z
  .object({ html: z.string().min(1), publicId: ArtifactPublicIdSchema.optional() })
  .strict()
export type ArtifactCheckRequest = z.infer<typeof ArtifactCheckRequestSchema>

/**
 * `findings` names the kind of each page-check finding, never the matched text. `emptyManifest` is the loud
 * warning for a page that declares no keys: markup built in JavaScript is invisible to extraction (KTD5).
 */
export const ArtifactCheckResponseSchema = z
  .object({
    ok: z.boolean(),
    findings: z.array(ArtifactPageCheckReasonSchema),
    manifest: ArtifactDataManifestSchema,
    emptyManifest: z.boolean(),
    wouldHide: z.array(ArtifactDataKeyRefSchema)
  })
  .strict()
export type ArtifactCheckResponse = z.infer<typeof ArtifactCheckResponseSchema>

// =============================================================================
// Version history (SCH-4882)
// =============================================================================

/**
 * One kept version, newest first in a list. `publishedBy` is the member's name (their subject once they are no longer
 * a member) and `publishedAt` when it was made; both are null (unknown) for a version recorded after the fact past
 * version 1 (an artifact from before history existed). `note` is untrusted text. `restoredFrom` is the version a
 * restore copied, null otherwise.
 */
export const ArtifactVersionSchema = z
  .object({
    version: VersionSchema,
    publishedBy: z.string().min(1).nullable(),
    publishedAt: EpochMsSchema.nullable(),
    note: z.string().max(ARTIFACT_VERSION_NOTE_MAX_LENGTH).nullable(),
    size: z.number().int().nonnegative(),
    restoredFrom: VersionSchema.nullable()
  })
  .strict()
export type ArtifactVersion = z.infer<typeof ArtifactVersionSchema>

/** `POST /artifacts/versions`: the kept versions of a live interactive artifact (at most 25). */
export const ArtifactVersionsRequestSchema = z.object({ publicId: ArtifactPublicIdSchema }).strict()
export type ArtifactVersionsRequest = z.infer<typeof ArtifactVersionsRequestSchema>

export const ArtifactVersionsResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    currentVersion: VersionSchema,
    versions: z.array(ArtifactVersionSchema),
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactVersionsResponse = z.infer<typeof ArtifactVersionsResponseSchema>

/** `aiArtifacts:versions`, the team-only member query: the same list, for the viewer's Versions panel. */
export const ArtifactMemberVersionsResponseSchema = z
  .object({ currentVersion: VersionSchema, versions: z.array(ArtifactVersionSchema) })
  .strict()
export type ArtifactMemberVersionsResponse = z.infer<typeof ArtifactMemberVersionsResponseSchema>

/**
 * `aiArtifacts:previewVersion`, team only: a five-minute content link to one kept version, framed like an open's
 * `contentUrl`. The page is never returned inline.
 */
export const ArtifactPreviewVersionResponseSchema = z
  .object({ version: VersionSchema, contentUrl: z.string().url(), expiresAt: EpochMsSchema })
  .strict()
export type ArtifactPreviewVersionResponse = z.infer<typeof ArtifactPreviewVersionResponseSchema>

/**
 * `POST /artifacts/restore`: version `version`'s page republished as the next version, keeping the title and school.
 * It refuses `would_hide_data` unless `allowHide`, as update does; its refusals are `ArtifactUpdateErrorResponseSchema`'s.
 * A restore that changes the page writes a `restored` audience event; restoring the current bytes answers
 * `changed: false`.
 */
export const ArtifactRestoreRequestSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    version: VersionSchema,
    allowHide: z.boolean().optional(),
    note: ArtifactVersionNoteSchema.optional(),
    thread: ThreadLabelSchema.optional()
  })
  .strict()
export type ArtifactRestoreRequest = z.infer<typeof ArtifactRestoreRequestSchema>

export const ArtifactRestoreResponseSchema = z
  .object({
    publicId: ArtifactPublicIdSchema,
    version: VersionSchema,
    changed: z.boolean(),
    restoredFrom: VersionSchema,
    viewerUrl: z.string().url()
  })
  .strict()
export type ArtifactRestoreResponse = z.infer<typeof ArtifactRestoreResponseSchema>

// =============================================================================
// Open
// =============================================================================

/**
 * `aiArtifacts:open`'s answer to the viewer. An open carries what the viewer shows outside the frame (R12)
 * and a short-lived signed content link (R13). A refusal carries the reason only; it is strict, so a title
 * can never ride along on one.
 *
 * `dataHandle` keys the outsider data subscription (`aiArtifactData:watch`, SCH-3664 KTD8) and `canEdit` says
 * whether the viewer may write. Both stay optional until the store sends them, so the viewer keeps parsing an
 * older store's answers. The viewer keeps the handle to itself; the frame never receives it (KTD7). Mirrored by
 * hand in apps/ops-store/convex/aiArtifactsContent.ts (`OpenResponse`); change both together.
 */
export const ArtifactOpenResponseSchema = z.discriminatedUnion('ok', [
  z
    .object({
      ok: z.literal(true),
      access: ArtifactAudienceSchema,
      title: z.string(),
      publishedBy: z.string(),
      publishedAt: EpochMsSchema,
      version: VersionSchema,
      contentUrl: z.string().url(),
      expiresAt: EpochMsSchema.optional(),
      dataHandle: z.string().min(1).max(128).optional(),
      canEdit: z.boolean().optional()
    })
    .strict(),
  z.object({ ok: z.literal(false), reason: ArtifactOpenRefusalReasonSchema }).strict()
])
export type ArtifactOpenResponse = z.infer<typeof ArtifactOpenResponseSchema>
